/**
 * Lifecycle do useChatSync: troca de conversa, cache, polling e fetch inicial.
 */
import { useEffect, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type { ChatMessage } from './types';

type GlobalMessageDetail = { conversationId?: string; message?: ChatMessage };

type LifecycleArgs = {
    isChatActive: boolean;
    activeConversationId: string | null;
    conversationDbId: string | null;
    connectionId?: string | null;
    isRealtimeConnected: boolean;
    setCurrentMessages: Dispatch<SetStateAction<ChatMessage[]>>;
    setIsFetchingMessages: Dispatch<SetStateAction<boolean>>;
    resetPagination: () => void;
    fetchMsgs: () => Promise<void>;
    scheduleRefresh: () => void;
    onDatabaseMessage: (message: ChatMessage) => void;
    activeIdRef: MutableRefObject<string | null>;
    lastRealConvRef: MutableRefObject<string | null>;
    cacheRef: MutableRefObject<Record<string, ChatMessage[]>>;
    pendingRefetchRef: MutableRefObject<boolean>;
    lastFetchDoneRef: MutableRefObject<number>;
};

export function useChatSyncLifecycle({
    isChatActive,
    activeConversationId,
    conversationDbId,
    connectionId,
    isRealtimeConnected,
    setCurrentMessages,
    setIsFetchingMessages,
    resetPagination,
    fetchMsgs,
    scheduleRefresh,
    onDatabaseMessage,
    activeIdRef,
    lastRealConvRef,
    cacheRef,
    pendingRefetchRef,
    lastFetchDoneRef,
}: LifecycleArgs) {
    useEffect(() => {
        if (!activeConversationId) {
            setCurrentMessages([]);
            setIsFetchingMessages(false);
            lastRealConvRef.current = null;
        } else if (activeConversationId !== lastRealConvRef.current) {
            lastRealConvRef.current = activeConversationId;
            const cached = cacheRef.current[activeConversationId];
            setCurrentMessages(cached || []);
            setIsFetchingMessages(!cached);
            resetPagination();
            pendingRefetchRef.current = false;
        }
        activeIdRef.current = activeConversationId;
    }, [activeConversationId, resetPagination, activeIdRef, lastRealConvRef, cacheRef, pendingRefetchRef, setCurrentMessages, setIsFetchingMessages]);


    useEffect(() => {
        if (!isChatActive || !activeConversationId) return;

        const onGlobal = (event: Event) => {
            const detail = (event as CustomEvent<GlobalMessageDetail>).detail;
            const eventConvId = detail?.conversationId;
            const isActiveConv = Boolean(eventConvId && conversationDbId && eventConvId === conversationDbId);
            if (isActiveConv && detail.message) onDatabaseMessage(detail.message);
            // Eventos de outra conversa não provocam nova consulta do histórico aberto.
            if (!eventConvId || !conversationDbId || isActiveConv) scheduleRefresh();
        };

        const onResume = () => { if (document.visibilityState === 'visible') scheduleRefresh(); };
        document.addEventListener('visibilitychange', onResume);
        window.addEventListener('online', onResume);
        window.addEventListener('chat:message_received', onGlobal);
        const intervalId = !isRealtimeConnected ? setInterval(scheduleRefresh, 8000) : undefined;
        return () => {
            document.removeEventListener('visibilitychange', onResume);
            window.removeEventListener('online', onResume);
            window.removeEventListener('chat:message_received', onGlobal);
            if (intervalId) clearInterval(intervalId);
        };
    }, [isChatActive, activeConversationId, conversationDbId, isRealtimeConnected, scheduleRefresh, onDatabaseMessage]);

    useEffect(() => {
        if (!isChatActive || !activeConversationId) return;
        let aborted = false;
        lastFetchDoneRef.current = 0;
        void (async () => { if (!aborted) await fetchMsgs(); })();
        return () => { aborted = true; };
    }, [isChatActive, fetchMsgs, activeConversationId, connectionId, lastFetchDoneRef]);
}
